import { createContext, useContext, useId, type ReactNode } from "react";
import { CircleAlert } from "lucide-react";

/**
 * What a control learns from the `Field` around it: its id (so the label is
 * always linked), the ids of the help / error text that describe it, and whether
 * it is invalid. A control outside any Field gets `null` and must name itself
 * with `aria-label`.
 */
interface FieldControl {
  id: string;
  describedBy: string | undefined;
  invalid: boolean;
}

const FieldContext = createContext<FieldControl | null>(null);

// The types cannot say "a Field around me, or an aria-label" (a control does not
// know its parent), so the two usage mistakes they let through are reported here
// in development: they are silent in the page and only an audit sees them. Each
// message names the control it is about (its type, name, placeholder) and is
// reported once per control: a message identical for every offender would hide
// all of them behind the first.
const warned = new Set<string>();
function warnOnce(message: string) {
  if (!import.meta.env.DEV || warned.has(message)) return;
  warned.add(message);
  console.warn(`[form] ${message}`);
}
function describeControl(props: { type?: string; name?: string; placeholder?: string }) {
  return (
    [props.type, props.name, props.placeholder && `"${props.placeholder}"`]
      .filter(Boolean)
      .join(" ") || "no type, name or placeholder to tell it apart"
  );
}

/**
 * The attributes a control must carry to belong to its `Field`. Explicit props
 * win only where the Field has nothing to say: the label's `htmlFor` targets the
 * Field's id, so an `id` passed to a control inside a Field would unlink it — it
 * is dropped, and reported in development.
 */
export function useControlAttrs(props: {
  id?: string;
  type?: string;
  name?: string;
  placeholder?: string;
  "aria-label"?: string;
  "aria-labelledby"?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean | "true" | "false" | "grammar" | "spelling";
}) {
  const field = useContext(FieldContext);
  if (!field && !props["aria-label"] && !props["aria-labelledby"]) {
    warnOnce(
      `A control outside a <Field> has no accessible name (${describeControl(props)}): wrap it in <Field label=…> or give it an aria-label.`,
    );
  }
  if (field && props.id) {
    warnOnce(
      `The id "${props.id}" passed to a control inside a <Field> (${describeControl(props)}) is ignored: the Field's own id is what links its label.`,
    );
  }
  const describedBy =
    [field?.describedBy, props["aria-describedby"]].filter(Boolean).join(" ") ||
    undefined;
  const invalid =
    !!field?.invalid ||
    props["aria-invalid"] === true ||
    props["aria-invalid"] === "true";
  return {
    attrs: {
      id: field?.id ?? props.id,
      "aria-describedby": describedBy,
      "aria-invalid": invalid ? (true as const) : undefined,
    },
    invalid,
  };
}

const hasContent = (node: ReactNode) =>
  node !== undefined && node !== null && node !== false && node !== "";

/**
 * One labelled control: label, control, then the help text — or, when there is
 * an error, the error in its place (an icon AND text, so it never rests on
 * colour alone). The label is always tied to the control (`htmlFor` = the id
 * the control reads from context) and the help / error text describes it
 * (`aria-describedby`).
 *
 * `hideLabel` keeps the label for assistive technology and drops it from the
 * layout, for a control whose meaning is plain from its surroundings (a filter
 * next to a table).
 */
export function Field({
  label,
  help,
  error,
  hideLabel,
  className,
  children,
}: {
  label: ReactNode;
  help?: ReactNode;
  error?: ReactNode;
  hideLabel?: boolean;
  className?: string;
  children: ReactNode;
}) {
  const id = useId();
  const helpId = `${id}-help`;
  const errorId = `${id}-error`;
  const hasError = hasContent(error);
  const hasHelp = hasContent(help);
  const describedBy = hasError ? errorId : hasHelp ? helpId : undefined;

  return (
    <FieldContext.Provider value={{ id, describedBy, invalid: hasError }}>
      <div className={className}>
        <label
          htmlFor={id}
          className={
            hideLabel
              ? "sr-only"
              : "mb-1 block text-sm font-medium text-ink"
          }
        >
          {label}
        </label>
        {children}
        {hasError ? (
          <p
            id={errorId}
            role="alert"
            className="mt-1 flex items-start gap-1 text-xs text-danger-fg"
          >
            <CircleAlert aria-hidden="true" className="mt-px h-3.5 w-3.5 shrink-0" />
            <span>{error}</span>
          </p>
        ) : hasHelp ? (
          <p id={helpId} className="mt-1 text-xs text-ink-muted">
            {help}
          </p>
        ) : null}
      </div>
    </FieldContext.Provider>
  );
}

/**
 * A group of related choices (a list of checkboxes, a set of radios): the
 * legend plays the label's part, and assistive technology reads it before each
 * choice. The help sits under the legend, before the choices. (No `error` yet:
 * nothing validates a group today — add it with the first form that does.)
 */
export function Fieldset({
  legend,
  help,
  className,
  children,
}: {
  legend: ReactNode;
  help?: ReactNode;
  className?: string;
  children: ReactNode;
}) {
  const id = useId();
  const helpId = `${id}-help`;
  const hasHelp = hasContent(help);
  return (
    <fieldset className={className} aria-describedby={hasHelp ? helpId : undefined}>
      <legend className="text-sm font-medium text-ink">{legend}</legend>
      {hasHelp && (
        <p id={helpId} className="mt-1 text-xs text-ink-muted">
          {help}
        </p>
      )}
      {children}
    </fieldset>
  );
}
